import React from 'react';
import { fireEvent, render, screen, waitFor } from '@testing-library/react';
import userEvent from '@testing-library/user-event';

import ConfigProvider from 'components/ConfigProvider';

import UsersFilters from './UsersFilters';
import { EMPTY_FILTERS } from '../urlState';

const ACCOUNTS = [
  { label: 'Account Name A', value: 1 },
  { label: 'Account Name B', value: 2 },
];
const PERMISSIONS = [
  { label: 'Fleet Administrator', value: 'Fleet Administrator' },
  { label: 'Member', value: 'Member' },
];
const JOB_ROLES = [
  { label: 'Fleet Manager', value: 'fleet_manager' },
  { label: 'Technician', value: 'technician' },
];

const renderFilters = (props = {}) => {
  const onChange = jest.fn();
  const onSearchAccounts = jest.fn();
  const onLoadMoreAccounts = jest.fn();

  render(
    <ConfigProvider>
      <UsersFilters
        accountOptions={ACCOUNTS}
        accountsTotal={ACCOUNTS.length}
        filters={EMPTY_FILTERS}
        jobRoleOptions={JOB_ROLES}
        permissionOptions={PERMISSIONS}
        onChange={onChange}
        onLoadMoreAccounts={onLoadMoreAccounts}
        onSearchAccounts={onSearchAccounts}
        {...props}
      />
    </ConfigProvider>,
  );

  return { onChange, onLoadMoreAccounts, onSearchAccounts };
};

const openMenu = (name) =>
  userEvent.click(screen.getByRole('button', { name: new RegExp(name, 'i') }));

/**
 * Scrolls the open menu's OptionsList <ul> to `remaining` px from its bottom.
 *
 * jsdom lays nothing out, so the three dimensions the handler reads are defined
 * here; without scrollHeight the handler bails, which is the guard that stops it
 * fetching the moment the menu opens.
 */
const scrollAccountsTo = (remaining) => {
  const list = document.querySelector('ul');
  const clientHeight = 100;
  const scrollHeight = 400;

  Object.defineProperty(list, 'clientHeight', {
    configurable: true,
    value: clientHeight,
  });
  Object.defineProperty(list, 'scrollHeight', {
    configurable: true,
    value: scrollHeight,
  });
  Object.defineProperty(list, 'scrollTop', {
    configurable: true,
    value: scrollHeight - clientHeight - remaining,
    writable: true,
  });

  fireEvent.scroll(list);
};

const openAccounts = async () => {
  openMenu('All Accounts');
  await waitFor(() =>
    expect(screen.getByText('Account Name A')).toBeInTheDocument(),
  );
};

describe('management/Users UsersFilters', () => {
  it('offers the three filters, without Teams', () => {
    renderFilters();

    expect(screen.getByText('All Accounts')).toBeInTheDocument();
    expect(screen.getByText('All Permissions')).toBeInTheDocument();
    expect(screen.getByText('All Job Roles')).toBeInTheDocument();
    // Teams wait on PD-4213.
    expect(screen.queryByText('All Teams')).not.toBeInTheDocument();
  });

  describe('applying a selection', () => {
    // The footer is the whole point of the staged draft: checking a box must not
    // refetch on its own.
    it('reports nothing until Apply is clicked', async () => {
      const { onChange } = renderFilters();

      openMenu('All Permissions');
      await waitFor(() =>
        expect(screen.getByText('Member')).toBeInTheDocument(),
      );

      fireEvent.click(screen.getByText('Member'));

      expect(onChange).not.toHaveBeenCalled();
    });

    it('reports the checked values on Apply', async () => {
      const { onChange } = renderFilters();

      openMenu('All Permissions');
      await waitFor(() =>
        expect(screen.getByText('Member')).toBeInTheDocument(),
      );

      fireEvent.click(screen.getByText('Member'));
      fireEvent.click(screen.getByRole('button', { name: /apply/i }));

      await waitFor(() =>
        expect(onChange).toHaveBeenCalledWith({
          ...EMPTY_FILTERS,
          permissions: ['Member'],
        }),
      );
    });

    it('empties the filter on Clear', async () => {
      const { onChange } = renderFilters({
        filters: { ...EMPTY_FILTERS, permissions: ['Member'] },
      });

      openMenu('Permissions');
      await waitFor(() =>
        expect(
          screen.getByRole('button', { name: /clear$/i }),
        ).toBeInTheDocument(),
      );

      fireEvent.click(screen.getByRole('button', { name: /clear$/i }));

      await waitFor(() =>
        expect(onChange).toHaveBeenCalledWith({
          ...EMPTY_FILTERS,
          permissions: [],
        }),
      );
    });
  });

  // Only Permissions gets this row.
  describe('the All row', () => {
    it('checks every permission at once', async () => {
      const { onChange } = renderFilters();

      openMenu('All Permissions');
      await waitFor(() => expect(screen.getByText('All')).toBeInTheDocument());

      fireEvent.click(screen.getByText('All'));
      fireEvent.click(screen.getByRole('button', { name: /apply/i }));

      await waitFor(() =>
        expect(onChange).toHaveBeenCalledWith({
          ...EMPTY_FILTERS,
          permissions: ['Fleet Administrator', 'Member'],
        }),
      );
    });

    it('is absent from the other menus', async () => {
      renderFilters();

      openMenu('All Job Roles');
      await waitFor(() =>
        expect(screen.getByText('Technician')).toBeInTheDocument(),
      );

      expect(screen.queryByText('All')).not.toBeInTheDocument();
    });
  });

  describe('searching', () => {
    // Accounts are global, so the server narrows them.
    it('asks the server when searching accounts', async () => {
      const { onSearchAccounts } = renderFilters();

      openMenu('All Accounts');
      await waitFor(() =>
        expect(
          screen.getByPlaceholderText('Search accounts'),
        ).toBeInTheDocument(),
      );

      fireEvent.change(screen.getByPlaceholderText('Search accounts'), {
        target: { value: 'name b' },
      });

      await waitFor(() => expect(onSearchAccounts).toHaveBeenCalled());
    });

    // Job roles are a bounded list, so they narrow in the browser.
    it('narrows job roles without a request', async () => {
      renderFilters();

      openMenu('All Job Roles');
      await waitFor(() =>
        expect(screen.getByText('Technician')).toBeInTheDocument(),
      );

      fireEvent.change(screen.getByPlaceholderText('Search job roles'), {
        target: { value: 'tech' },
      });

      await waitFor(() =>
        expect(screen.queryByText('Fleet Manager')).not.toBeInTheDocument(),
      );
      expect(screen.getByText('Technician')).toBeInTheDocument();
    });

    it('gives Permissions no search input', async () => {
      renderFilters();

      openMenu('All Permissions');
      await waitFor(() =>
        expect(screen.getByText('Member')).toBeInTheDocument(),
      );

      expect(screen.queryByPlaceholderText(/search/i)).not.toBeInTheDocument();
    });
  });

  // PD-4577: a large OEM segment runs to hundreds of accounts, so the menu holds
  // however many pages have been loaded rather than all of them.
  describe('paging the Accounts menu', () => {
    const partial = { accountsTotal: 500, hasMoreAccounts: true };

    it('says how much of the list is loaded', async () => {
      renderFilters(partial);

      await openAccounts();

      expect(screen.getByText('Showing 2 of 500 accounts')).toBeInTheDocument();
    });

    // Worded without "more" because the same flag covers a search reloading the
    // list, which replaces the options rather than appending to them.
    it('says so while a page is on its way', async () => {
      renderFilters({ ...partial, isLoadingAccounts: true });

      await openAccounts();

      expect(screen.getByText('Loading accounts...')).toBeInTheDocument();
    });

    // Nothing to disclose once the menu holds every match, and a status line
    // there would only be noise.
    it('says nothing once every account is loaded', async () => {
      renderFilters();

      await openAccounts();

      expect(screen.queryByText(/showing/i)).not.toBeInTheDocument();
    });

    it('asks for the next page as the list nears its bottom', async () => {
      const { onLoadMoreAccounts } = renderFilters(partial);

      await openAccounts();
      scrollAccountsTo(10);

      expect(onLoadMoreAccounts).toHaveBeenCalled();
    });

    it('leaves it alone while the bottom is still far off', async () => {
      const { onLoadMoreAccounts } = renderFilters(partial);

      await openAccounts();
      scrollAccountsTo(300);

      expect(onLoadMoreAccounts).not.toHaveBeenCalled();
    });

    // jsdom reports every dimension as 0, which without the scrollHeight guard
    // reads as "already at the bottom" — so opening the menu would fetch.
    it('does not fetch merely because the menu opened', async () => {
      const { onLoadMoreAccounts } = renderFilters(partial);

      await openAccounts();
      fireEvent.scroll(document.querySelector('ul'));

      expect(onLoadMoreAccounts).not.toHaveBeenCalled();
    });

    it('stops asking once everything is loaded', async () => {
      const { onLoadMoreAccounts } = renderFilters();

      await openAccounts();
      scrollAccountsTo(10);

      expect(onLoadMoreAccounts).not.toHaveBeenCalled();
    });

    // Permissions and Job Role hold their whole lists, so neither pages nor
    // reports a count.
    it('leaves the unpaged menus without a status line', async () => {
      renderFilters(partial);

      openMenu('All Job Roles');
      await waitFor(() =>
        expect(screen.getByText('Technician')).toBeInTheDocument(),
      );

      expect(screen.queryByText(/showing/i)).not.toBeInTheDocument();
    });
  });

  describe('Clear All', () => {
    it('is hidden while nothing is filtered', () => {
      renderFilters();

      expect(
        screen.queryByRole('button', { name: /clear all/i }),
      ).not.toBeInTheDocument();
    });

    it('appears once a filter is active and empties them all', () => {
      const { onChange } = renderFilters({
        filters: { ...EMPTY_FILTERS, jobRoles: ['technician'] },
      });

      userEvent.click(screen.getByRole('button', { name: /clear all/i }));

      expect(onChange).toHaveBeenCalledWith(EMPTY_FILTERS);
    });
  });
});
